import apiClient, { ApiResponse, PaginatedResponse } from '../api/client';
import { User } from '../types/models';

export interface UserFilters {
  search?: string;
  role_id?: number;
  is_active?: boolean;
  page?: number;
  per_page?: number;
}

export interface CreateUserData {
  first_name: string;
  last_name: string;
  middle_name?: string;
  email: string;
  phone?: string;
  roles?: number[];
  permissions?: number[];
  is_active?: boolean;
}

export interface UpdateUserData {
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  password?: string;
  password_confirmation?: string;
  is_active?: boolean;
}

class UserService {
  private baseUrl = '/admin/county/users';

  async getUsers(filters?: UserFilters): Promise<ApiResponse<PaginatedResponse<User>>> {
    const params = new URLSearchParams();
    if (filters?.search) params.append('search', filters.search);
    if (filters?.role_id) params.append('role_id', filters.role_id.toString());
    if (filters?.is_active !== undefined) params.append('is_active', filters.is_active.toString());
    if (filters?.page) params.append('page', filters.page.toString());
    if (filters?.per_page) params.append('per_page', filters.per_page.toString());

    return apiClient.get<PaginatedResponse<User>>(`${this.baseUrl}?${params.toString()}`);
  }

  async getUser(id: number): Promise<ApiResponse<User>> {
    return apiClient.get<User>(`${this.baseUrl}/${id}`);
  }

  async createUser(data: CreateUserData): Promise<ApiResponse<User>> {
    return apiClient.post<User>(this.baseUrl, data);
  }

  async updateUser(id: number, data: UpdateUserData): Promise<ApiResponse<User>> {
    return apiClient.put<User>(`${this.baseUrl}/${id}`, data);
  }

  async deleteUser(id: number): Promise<ApiResponse> {
    return apiClient.delete(`${this.baseUrl}/${id}`);
  }

  async toggleStatus(id: number): Promise<ApiResponse<User>> {
    return apiClient.post<User>(`${this.baseUrl}/${id}/toggle-status`);
  }

  async assignRoles(id: number, roleIds: number[]): Promise<ApiResponse<User>> {
    return apiClient.post<User>(`${this.baseUrl}/${id}/roles`, { role_ids: roleIds });
  }

  async syncRoles(id: number, roleIds: number[]): Promise<ApiResponse<User>> {
    return apiClient.put<User>(`${this.baseUrl}/${id}/roles`, { role_ids: roleIds });
  }

  async removeRole(id: number, roleId: number): Promise<ApiResponse<User>> {
    return apiClient.delete(`${this.baseUrl}/${id}/roles/${roleId}`);
  }
}

export const userService = new UserService();
export default userService;
